import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    icon = "icon",
    title = "title",
    description = "description",
    items = "items",
    btnLabel = "btnLabel",
    buttonLink = "buttonLink",
}

type ItemModel = {
    fields: {
        icon?: string;
        title?: string;
        description?: string;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.icon]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.items]: ItemModel[];
        [fields.btnLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
    };
}

type Props = OwnProps;

export interface ColonyDifferenceStyles {
    mainGridContainer: GridContainerProps;
    titleLabel?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    ctaButton?: ButtonPresentationProps;
    detailsContainer?: GridItemProps;
    imageContainer?: GridItemProps;
    image?: ImgProps;
    headingWrapper?: InjectableCss;
    customerCompanyText?: TypographyPresentationProps;
    icon?: ImgProps;
    headingGridItem?: GridItemProps;
    mainTitle?: TypographyPresentationProps;
}

export const colonyDifferenceStyles: ColonyDifferenceStyles = {
    mainGridContainer: {
        css: css`
            width: 100%;
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 24px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    icon: {
        css: css`
            // height: 48px;
            height: 0px;
            width: 48px;
        `,
    },
    titleLabel: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    descriptionText: {
        css: css`
            margin-bottom: 1rem;
            text-align: center;
        `,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            padding-top: 15px;
            justify-content: center;
        `,
    },
    ctaButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        null,
                        css`
                            width: auto;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    detailsContainer: {
        width: [12, 12, 6, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 16px;
        `,
    },
    imageContainer: {
        width: [12, 12, 6, 6, 6],
    },
    image: {
        css: css`
            width: 100%;
            max-width: 100%;
        `,
    },
    headingWrapper: {
        css: css`
            margin-bottom: 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            width: 100%;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    mainTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            width: 100%;
            margin-bottom: 1.5rem;
            margin-top: 25px;
        `,
    },
};

export const ColonyDifference = ({ fields }: Props) => {
    const styles = colonyDifferenceStyles;
    const [domain, setDomain] = useState("");
    const { url } = useGetLink(fields.buttonLink);
    const { isInShell } = useShellContext();
    const history = useHistory();

    const handleBtnClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    useEffect(() => {
        if (window) {
            setDomain(`${window?.location?.protocol}//${window?.location?.host}`);
        }
    }, []);

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.mainTitle}>{fields.heading}</Typography>
            </GridItem>
            {fields.items.map(item => {
                const iconUrl = `${domain}${item.fields.icon}`;
                return (
                    <GridItem {...styles.detailsContainer} key={item.fields.title}>
                        <StyledWrapper {...styles.headingWrapper}>
                            {/* <Icon src={iconUrl} size={48} /> */}
                            <Typography {...styles.titleLabel}>{item.fields.title}</Typography>
                        </StyledWrapper>
                        <Typography {...styles.descriptionText}>
                            {item.fields.description ? parse(item.fields.description, parserOptions) : ""}
                        </Typography>
                    </GridItem>
                );
            })}
            <GridItem {...styles.buttonGridItem}>
                <Button {...styles.ctaButton} onClick={() => handleBtnClick(url)}>
                    {fields.btnLabel}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: ColonyDifference,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "The Colony Defference",
                fieldType: "Translatable",
            },
            {
                name: fields.items,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: fields.icon,
                        displayName: "Icon",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.buttonLink,
                fieldType: "Contextual",
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.btnLabel,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Create an account",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;